import { Directive, model } from '@angular/core';
import { FormCheckboxControl } from '@angular/forms/signals';
import { FormControlStateAbstract } from './form-control-state-abstract';

/**
 * Base directive for every Juno control whose whole value is a **boolean**: a
 * single checkbox, a toggle, a switch.
 *
 * All validation-state and presentational inputs live on
 * {@link FormControlStateAbstract}. This class adds only what the
 * `FormCheckboxControl` contract requires: a `checked` model signal, which
 * `[formField]` binds to the field value instead of `value`.
 *
 * A control on this contract **must not** declare a `value` member:
 * `FormCheckboxControl` types it as `value?: undefined`, and `[formField]` uses
 * its presence to decide the control is a standard input.
 *
 * This is the narrow branch of the tree. A group of checkboxes is *not* a
 * checkbox control — its field value is an array, so the group extends
 * {@link InputValueClassAbstract} with `T = string[]` and the individual boxes
 * inside it are presentational children, not form controls. The same holds for
 * radio: the group is the control, the buttons are not.
 *
 * @template CL The shape of the optional `appliedClasses` object that lets
 *              consumers customise internal CSS classes at the call site.
 *
 * @implements {FormCheckboxControl}
 *
 * @usageNotes
 * ```ts
 * @Component({ selector: 'juno-libs-ui-checkbox', ... })
 * export class JunoCheckbox extends FormCheckClassAbstract<string> {}
 * ```
 *
 * ```html
 * <!-- Signal Forms -->
 * <juno-libs-ui-checkbox [formField]="userForm.acceptTerms" label="TERMS.ACCEPT" />
 *
 * <!-- Standalone two-way binding -->
 * <juno-libs-ui-checkbox [(checked)]="rememberMe" label="LOGIN.REMEMBER_ME" />
 * ```
 *
 * @see {@link FormControlStateAbstract} for the inherited state inputs.
 * @see {@link InputValueClassAbstract} for value-based controls, including groups.
 */
@Directive()
export abstract class FormCheckClassAbstract<CL = unknown>
  extends FormControlStateAbstract<CL>
  implements FormCheckboxControl
{
  checked = model<boolean>(false);
}
